import { ALL_FORMATS, Input, UrlSource } from "mediabunny";
import type { ScriptLayerInputConfig } from "../../../engine/clips/script/script.interfaces";
import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { CompositionStateStore } from "../../../librettoEditor/store/CompositionStateStore";
import { maybeLockCanvasToFirstVideo } from "../../../utils/canvasSizing";
import { CreateScriptClipForComposition } from "../utils";
import {
    calculateVideoVisualDimensions,
    parseVideoResolution,
    resolveVideoDimensionsFromInput,
} from "./videoSizing";

type ResolveTrackUrlFn = (
    trackId: string,
    type: "audio" | "video",
) => Promise<string | undefined>;

type InsertTrackIntoScriptLayerArgs = {
    trackId: string;
    trackType: "audio" | "video";
    trackTitle?: string | null;
    trackResolution?: string | null;
    resolveTrackUrl: ResolveTrackUrlFn;
};

export async function insertTrackIntoScriptLayer({
    trackId,
    trackType,
    trackTitle,
    trackResolution,
    resolveTrackUrl,
}: InsertTrackIntoScriptLayerArgs): Promise<any | null> {
    const normalizedTrackId = String(trackId ?? "").trim();
    const isVideo = trackType === "video";
    if (!normalizedTrackId) {
        return null;
    }

    const url = await resolveTrackUrl(normalizedTrackId, isVideo ? "video" : "audio");
    if (!url) {
        return null;
    }

    const composition = CompositionService.composition;
    if (!composition) {
        return null;
    }

    const input = new Input({
        source: new UrlSource(url),
        formats: ALL_FORMATS,
    });

    let { width: trackWidth, height: trackHeight } = parseVideoResolution(trackResolution);
    if (isVideo && (!(trackWidth > 0) || !(trackHeight > 0))) {
        const resolvedDims = await resolveVideoDimensionsFromInput(input);
        if (resolvedDims.width > 0 && resolvedDims.height > 0) {
            trackWidth = resolvedDims.width;
            trackHeight = resolvedDims.height;
        }
    }

    if (isVideo && trackWidth > 0 && trackHeight > 0) {
        maybeLockCanvasToFirstVideo(composition, trackWidth, trackHeight);
    }

    const compWidth = composition?.width ?? 1920;
    const compHeight = composition?.height ?? 1080;

    const config: ScriptLayerInputConfig = {
        input,
        assetId: normalizedTrackId,
        name: "",
        color: "#3B82F6",
        gain: 1.0,
    };

    if (isVideo) {
        const visualDims = calculateVideoVisualDimensions(
            trackWidth,
            trackHeight,
            compWidth,
            compHeight,
        );
        config.visual = {
            x: visualDims.x,
            y: visualDims.y,
            width: visualDims.width,
            height: visualDims.height,
            alpha: 1.0,
            ...(trackWidth > 0 && trackHeight > 0
                ? {
                      originalWidth: trackWidth,
                      originalHeight: trackHeight,
                  }
                : {}),
        } as any;
    }

    const scriptLayerClip = await CreateScriptClipForComposition({
        inputConfigs: [config],
        composition,
        stateStore: CompositionStateStore,
        clipName: String(trackTitle ?? "").trim() || undefined,
    });
    if (!scriptLayerClip) {
        return null;
    }

    if (isVideo && trackWidth > 0 && trackHeight > 0) {
        CompositionStateStore.setOriginalMediaResolutionForClip(
            scriptLayerClip.id,
            trackWidth,
            trackHeight,
        );
    }

    const addedScriptClip = await CompositionService.addToScriptLayer(scriptLayerClip);
    return addedScriptClip ?? scriptLayerClip;
}
